
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { from as observableFrom, Observable, EMPTY } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { noHeadersPaths, whiteListedPaths } from './auth.constants';
import { AuthService } from '@app/services/auth.service';
import { Store } from '@ngrx/store';
import { AppState } from '@app/store/app.reducers';
import { TokenExpired } from '@app/store/auth/auth.actions';
import { AuthConstants } from '@app/constants/application-constants';
import { AuthToken } from '@app/model/auth/auth-token.model';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    whitelistedPaths: Array<string | RegExp> = [];
    whitelistedDomains: Array<string | RegExp> = [];
    noHeadersPaths: Array<RegExp> = [];

    constructor(private router: Router, private auth: AuthService, private store: Store<AppState>) {
        this.whitelistedPaths = whiteListedPaths;
        this.noHeadersPaths = noHeadersPaths;
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const token = AuthService.getAuthTokenFromLocalStorage();

        return this.handleInterception(token, request, next);
    }

    handleInterception(
        token: AuthToken,
        request: HttpRequest<any>,
        next: HttpHandler
    ) {
        if (!!token && pathRequiresAuthHeader(request.url)) {
            if (isTokenValid(token)) {
                if (!requestHasAuthHeader(request)) {
                    request = request.clone({
                        setHeaders: {
                            ['Authorization']: `Bearer ${token.accessToken}`
                        }
                    });
                }
            } else {
                // Token has expired, so cancel the request and redirect the user to the login page
                this.store.dispatch(new TokenExpired());
                return EMPTY;
            }
        }

        return next.handle(request).pipe(
            tap(() => {},
                (err: HttpErrorResponse) => {
                    if (err.status === 401 && !err.url.includes('/auth/login')) {
                        this.store.dispatch(new TokenExpired());
                    }
                }
            ));
    }

    isWhitelistedPaths(request: HttpRequest<any>): boolean {
        const requestUrl = request.url;
        const requestDomainMatch = requestUrl.match(/^(?:https?:\/\/)?(?:[^@\/\n]+@)?(?:www\.)?([^:\/?\n]+)/img);
        const requestDomain = !!requestDomainMatch ? requestDomainMatch[0] : '';

        if (this.whitelistedPaths.length > 0) {
            return ( // path format is [domain]/[path]
                findIndex(
                    path => typeof path === 'string'
                        ? `${request.url}`.indexOf(path) > -1
                        : path instanceof RegExp ? path.test(`${request.url}`) : false
                    , this.whitelistedPaths) > -1
            );
        }

        return (
            findIndex(
                domain =>
                    typeof domain === 'string'
                        ? domain === requestDomain
                        : domain instanceof RegExp ? domain.test(requestDomain) : false
                , this.whitelistedDomains) > -1
        );
    }
}

const findIndex = function (predicate, array) {
    if (array == null) {
        throw new TypeError('"this" is null or not defined');
    }
    const o = array;
    const len = o.length;

    if (len === 0) {
        return -1;
    }

    if (typeof predicate !== 'function') {
        throw new TypeError('predicate must be a function');
    }

    const thisArg = o;

    let k = 0;

    while (k < len) {
        const kValue = o[k];
        if (predicate.call(thisArg, kValue, k, o)) {
            return k;
        }
        k++;
    }

    return -1;
};

const isTokenValid = (token: AuthToken) => (new Date().getTime() / 1000) < token.expiresAt;

const requestHasAuthHeader = (request: HttpRequest<any>) => request.headers.has('Authorization');

const pathRequiresAuthHeader = (path: string) => !noHeadersPaths.some(noHeaderPath => noHeaderPath.test(path));
